import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { Button, Heading, Icon, Icons, Link, LinkButton, Text } from "@phoenix/components";
import { useNotifyError, useNotifySuccess } from "@phoenix/contexts";
import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

const pageContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

const innerContent = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-150);
  padding: var(--global-dimension-size-150);
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const breadcrumbRow = css`
  display: flex;
  gap: var(--global-dimension-size-75);
  align-items: center;
  flex-wrap: wrap;
`;

const breadcrumbLink = css`
  font-size: 0.875rem;
  color: var(--color-text-500);
  text-decoration: none;
  &:hover {
    color: var(--color-text-900);
    text-decoration: underline;
  }
`;

const heroGrid = css`
  display: grid;
  gap: var(--global-dimension-size-50);
  margin-bottom: var(--global-dimension-size-50);
`;

const templatesGrid = css`
  display: grid;
  gap: var(--global-dimension-size-150);
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  width: 100%;
`;

const templateCard = css`
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  border: 1.5px solid rgba(16, 185, 129, 0.12);
  background: var(--global-color-gray-75);
  padding: var(--global-dimension-size-150);
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.03);
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  min-height: 380px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(16, 185, 129, 0.08);
    border-color: rgba(16, 185, 129, 0.3);
  }
`;

const iconBadge = css`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: rgba(16, 185, 129, 0.08);
  color: var(--color-success-900, #059669);
`;

const badgeRow = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const statusBadge = css`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const statusSuccess = css`
  ${statusBadge};
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
`;

const statusStopped = css`
  ${statusBadge};
  background: rgba(100, 116, 139, 0.1);
  color: var(--color-text-500);
`;

const statusDot = css`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: currentColor;
`;

const cardHeader = css`
  display: flex;
  flex-direction: column;
  margin-top: var(--global-dimension-size-100);
  margin-bottom: var(--global-dimension-size-100);
`;

const cardTitle = css`
  font-size: 1.35rem;
  font-weight: 750;
  color: var(--color-text-900);
  margin-bottom: var(--global-dimension-size-50);
`;

const cardDescription = css`
  font-size: 13px !important;
  line-height: 1.5 !important;
  font-weight: 400 !important;
  color: var(--color-text-500, #64748b) !important;
`;

const labelText = css`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-400);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: var(--global-dimension-size-50);
`;

const tagContainer = css`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: var(--global-dimension-size-100);
`;

const miniTag = css`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.06);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.1);
`;

const cardFooter = css`
  margin-top: auto;
  width: 100%;
`;

const selectButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.85rem 1.25rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 200ms ease;
  box-sizing: border-box;

  background: rgba(16, 185, 129, 0.06);
  color: var(--color-success-950, #047857);
  border: 1px solid rgba(16, 185, 129, 0.14);

  &:hover {
    background: var(--color-success-900, #059669);
    color: white;
    border-color: transparent;
  }
`;

const actionButtons = css`
  display: flex;
  gap: var(--global-dimension-size-50);
  width: 100%;
`;

export function DatasetTemplatesPage() {
  const notifySuccess = useNotifySuccess();
  const notifyError = useNotifyError();
  const [statuses, setStatuses] = useState<Record<string, "running" | "stopped">>({
    "ground-truth": "stopped",
    "llm-assisted": "stopped",
    "reasoning": "stopped",
    "regression-testing": "stopped",
    "output-drift": "stopped",
  });

  useEffect(() => {
    let active = true;

    async function checkStatuses() {
      try {
        const [gtRes, llmRes, reasoningRes, regressionRes, driftRes] = await Promise.all([
          authFetch(prependBasename("/v1/templates/dataset-status/ground-truth")),
          authFetch(prependBasename("/v1/templates/dataset-status/llm-assisted")),
          authFetch(prependBasename("/v1/templates/dataset-status/reasoning")),
          authFetch(prependBasename("/v1/templates/dataset-status/regression-testing")),
          authFetch(prependBasename("/v1/templates/dataset-status/output-drift")),
        ]);

        if (gtRes.ok && llmRes.ok && reasoningRes.ok && regressionRes.ok && driftRes.ok) {
          const gtData = await gtRes.json();
          const llmData = await llmRes.json();
          const reasoningData = await reasoningRes.json();
          const regressionData = await regressionRes.json();
          const driftData = await driftRes.json();
          if (active) {
            setStatuses({
              "ground-truth": gtData.status === "running" ? "running" : "stopped",
              "llm-assisted": llmData.status === "running" ? "running" : "stopped",
              "reasoning": reasoningData.status === "running" ? "running" : "stopped",
              "regression-testing": regressionData.status === "running" ? "running" : "stopped",
              "output-drift": driftData.status === "running" ? "running" : "stopped",
            });
          }
        }
      } catch (err) {
        console.error("Failed to fetch dataset template statuses:", err);
      }
    }

    checkStatuses();
    
    // Set up a poll interval to update status every 4 seconds in the background
    const interval = setInterval(checkStatuses, 4000);
    
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const handleStopEvaluation = async (templateId: string) => {
    try {
      const res = await authFetch(prependBasename("/v1/templates/stop-dataset"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ template_id: templateId }),
      });

      if (!res.ok) {
        throw new Error("Failed to stop evaluation");
      }

      let friendlyName = "Reasoning";
      if (templateId === "ground-truth") friendlyName = "Ground-Truth";
      else if (templateId === "llm-assisted") friendlyName = "LLM-Assisted";
      else if (templateId === "regression-testing") friendlyName = "Regression Testing";
      else if (templateId === "output-drift") friendlyName = "Output Drift Detection";

      notifySuccess({
        title: "Evaluation stopped",
        message: `${friendlyName} evaluation has been stopped successfully.`,
      });

      setStatuses((prev) => ({
        ...prev,
        [templateId]: "stopped",
      }));
    } catch (err) {
      notifyError({
        title: "Failed to stop evaluation",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  return (
    <div css={pageContainer}>
      <div css={innerContent}>
      <div css={breadcrumbRow}>
        <Link css={breadcrumbLink} to="/templates">
          Templates
        </Link>
        <Text color="text-400">/</Text>
        <Text color="text-500">Dataset Templates</Text>
      </div>

      <div css={heroGrid}>
        <div>
          <Heading level={1}>Dataset Evaluation Templates</Heading>
          <Text color="text-500" size="M" css={css`margin-top: var(--global-dimension-size-25);`}>
            Evaluate model outputs against reference answers or grade response quality using language models.
          </Text>
        </div>

        {/* ── Regression Testing Card ── */}
        {/* <div css={templateCard}>
          <div>
            <div css={css`display: flex; justify-content: space-between; align-items: start;`}>
              <div css={iconBadge}>
                <Icon svg={<Icons.DatabaseOutline />} />
              </div>
              <div css={badgeRow}>
                {statuses["regression-testing"] === "running" ? (
                  <span css={statusSuccess}>
                    <span css={statusDot} />
                    Running
                  </span>
                ) : (
                  <span css={statusStopped}>Stopped</span>
                )}
              </div>
            </div>
            <div css={cardHeader}>
              <Heading level={2} css={cardTitle}>Regression Testing</Heading>
              <Text css={cardDescription}>
                Run regression tests and output-drift detection across experiments and baselines.
              </Text>
            </div>
            <div>
              <div css={labelText}>Included Evaluators</div>
              <div css={tagContainer}>
                <span css={miniTag}>Output Drift</span>
                <span css={miniTag}>Baseline Comparison</span>
                <span css={miniTag}>Regression Alerts</span>
              </div>
            </div>
          </div>
          <div css={cardFooter}>
            {statuses["regression-testing"] === "running" ? (
              <div css={actionButtons}>
                <LinkButton 
                  to="/templates/dataset/regression-testing" 
                  css={css`
                    ${selectButtonStyles};
                    flex: 1;
                  `}
                >
                  <span>Configure</span>
                  <Icon svg={<Icons.ArrowRight />} />
                </LinkButton>
                <Button
                  variant="danger"
                  size="M"
                  css={css`
                    border-radius: 14px;
                    font-weight: 600;
                  `}
                  onPress={() => handleStopEvaluation("regression-testing")}
                >
                  Stop
                </Button>
              </div>
            ) : (
              <LinkButton 
                to="/templates/dataset/regression-testing" 
                css={selectButtonStyles}
              >
                <span>Configure Template</span>
                <Icon svg={<Icons.ArrowRight />} />
              </LinkButton>
            )}
          </div>
        </div> */}
      </div>

      <div css={templatesGrid}>
        {/* ── Ground-Truth Evaluation Card ── */}
        <div css={templateCard}>
          <div>
            <div css={css`display: flex; justify-content: space-between; align-items: start;`}>
              <div css={iconBadge}>
                <Icon svg={<Icons.DatabaseOutline />} />
              </div>
              <div css={badgeRow}>
                {statuses["ground-truth"] === "running" ? (
                  <span css={statusSuccess}>
                    <span css={statusDot} />
                    Running
                  </span>
                ) : (
                  <span css={statusStopped}>Stopped</span>
                )}
              </div>
            </div>
            <div css={cardHeader}>
              <Heading level={2} css={cardTitle}>Ground-Truth Evaluation</Heading>
              <Text css={cardDescription}>
                Evaluate model outputs against reference answers using multiple ground-truth metrics.
              </Text>
            </div>
            <div>
              <div css={labelText}>Included Evaluators</div>
              <div css={tagContainer}>
                <span css={miniTag}>Exact Match</span>
                <span css={miniTag}>F1 Score</span>
                <span css={miniTag}>Answer Correctness</span>
                <span css={miniTag}>ROUGE</span>
                <span css={miniTag}>BLEU</span>
              </div>
            </div>
          </div>
          <div css={cardFooter}>
            {statuses["ground-truth"] === "running" ? (
              <div css={actionButtons}>
                <LinkButton 
                  to="/templates/dataset/ground-truth" 
                  css={css`
                    ${selectButtonStyles};
                    flex: 1;
                  `}
                >
                  <span>Configure</span>
                  <Icon svg={<Icons.ArrowRight />} />
                </LinkButton>
                <Button
                  variant="danger"
                  size="M"
                  css={css`
                    border-radius: 14px;
                    font-weight: 600;
                  `}
                  onPress={() => handleStopEvaluation("ground-truth")}
                >
                  Stop
                </Button>
              </div>
            ) : (
              <LinkButton 
                to="/templates/dataset/ground-truth" 
                css={selectButtonStyles}
              >
                <span>Configure Template</span>
                <Icon svg={<Icons.ArrowRight />} />
              </LinkButton>
            )}
          </div>
        </div>

        {/* ── LLM-Assisted Evaluation Card ── */}
        <div css={templateCard}>
          <div>
            <div css={css`display: flex; justify-content: space-between; align-items: start;`}>
              <div css={iconBadge}>
                <Icon svg={<Icons.Brain />} />
              </div>
              <div css={badgeRow}>
                {statuses["llm-assisted"] === "running" ? (
                  <span css={statusSuccess}>
                    <span css={statusDot} />
                    Running
                  </span>
                ) : (
                  <span css={statusStopped}>Stopped</span>
                )}
              </div>
            </div>
            <div css={cardHeader}>
              <Heading level={2} css={cardTitle}>Automated Model Evaluation</Heading>
              <Text css={cardDescription}>
                Grade model outputs using a judge model to evaluate correctness, instruction-following, helpfulness, and reasoning.
              </Text>
            </div>
            <div>
              <div css={labelText}>Included Evaluators</div>
              <div css={tagContainer}>
                <span css={miniTag}>LLM Correctness</span>
                <span css={miniTag}>Relevance</span>
                <span css={miniTag}>Helpfulness</span>
                <span css={miniTag}>Instruction Following</span>
                <span css={miniTag}>Reasoning Quality</span>
                <span css={miniTag}>CoT Quality</span>
              </div>
            </div>
          </div>
          <div css={cardFooter}>
            {statuses["llm-assisted"] === "running" ? (
              <div css={actionButtons}>
                <LinkButton 
                  to="/templates/dataset/llm-assisted" 
                  css={css`
                    ${selectButtonStyles};
                    flex: 1;
                  `}
                >
                  <span>Configure</span>
                  <Icon svg={<Icons.ArrowRight />} />
                </LinkButton>
                <Button
                  variant="danger"
                  size="M"
                  css={css`
                    border-radius: 14px;
                    font-weight: 600;
                  `}
                  onPress={() => handleStopEvaluation("llm-assisted")}
                >
                  Stop
                </Button>
              </div>
            ) : (
              <LinkButton 
                to="/templates/dataset/llm-assisted" 
                css={selectButtonStyles}
              >
                <span>Configure Template</span>
                <Icon svg={<Icons.ArrowRight />} />
              </LinkButton>
            )}
          </div>
        </div>

        {/* ── Reasoning Evaluation Card ── */}
        <div css={templateCard}>
          <div>
            <div css={css`display: flex; justify-content: space-between; align-items: start;`}>
              <div css={iconBadge}>
                <Icon svg={<Icons.BookOutline />} />
              </div>
              <div css={badgeRow}>
                {statuses["reasoning"] === "running" ? (
                  <span css={statusSuccess}>
                    <span css={statusDot} />
                    Running
                  </span>
                ) : (
                  <span css={statusStopped}>Stopped</span>
                )}
              </div>
            </div>
            <div css={cardHeader}>
              <Heading level={2} css={cardTitle}>Reasoning Evaluation</Heading>
              <Text css={cardDescription}>
                Evaluate model reasoning capabilities, logical progression, causal dependencies, and mathematical accuracy.
              </Text>
            </div>
            <div>
              <div css={labelText}>Included Evaluators</div>
              <div css={tagContainer}>
                <span css={miniTag}>Logical Correctness</span>
                <span css={miniTag}>Math Accuracy</span>
                <span css={miniTag}>Step-by-Step Reasoning</span>
                <span css={miniTag}>Causal Reasoning</span>
                <span css={miniTag}>Multi-Hop Reasoning</span>
              </div>
            </div>
          </div>
          <div css={cardFooter}>
            {statuses["reasoning"] === "running" ? (
              <div css={actionButtons}>
                <LinkButton 
                  to="/templates/dataset/reasoning" 
                  css={css`
                    ${selectButtonStyles};
                    flex: 1;
                  `}
                >
                  <span>Configure</span>
                  <Icon svg={<Icons.ArrowRight />} />
                </LinkButton>
                <Button
                  variant="danger"
                  size="M"
                  css={css`
                    border-radius: 14px;
                    font-weight: 600;
                  `}
                  onPress={() => handleStopEvaluation("reasoning")}
                >
                  Stop
                </Button>
              </div>
            ) : (
              <LinkButton 
                to="/templates/dataset/reasoning" 
                css={selectButtonStyles}
              >
                <span>Configure Template</span>
                <Icon svg={<Icons.ArrowRight />} />
              </LinkButton>
            )}
          </div>
        </div>

        {/* ── Output Drift Detection Card ── */}
        <div css={templateCard}>
          <div>
            <div css={css`display: flex; justify-content: space-between; align-items: start;`}>
              <div css={iconBadge}>
                <Icon svg={<Icons.EyeOutline />} />
              </div>
              <div css={badgeRow}>
                {statuses["output-drift"] === "running" ? (
                  <span css={statusSuccess}>
                    <span css={statusDot} />
                    Running
                  </span>
                ) : (
                  <span css={statusStopped}>Stopped</span>
                )}
              </div>
            </div>
            <div css={cardHeader}>
              <Heading level={2} css={cardTitle}>Output Drift Detection</Heading>
              <Text css={cardDescription}>
                Compare actual outputs against reference outputs to detect semantic drift, factual consistency, and information coverage.
              </Text>
            </div>
            <div>
              <div css={labelText}>Included Evaluators</div>
              <div css={tagContainer}>
                <span css={miniTag}>Semantic Meaning</span>
                <span css={miniTag}>Factual Consistency</span>
                <span css={miniTag}>Information Coverage</span>
              </div>
            </div>
          </div>
          <div css={cardFooter}>
            {statuses["output-drift"] === "running" ? (
              <div css={actionButtons}>
                <LinkButton 
                  to="/templates/dataset/output-drift" 
                  css={css`
                    ${selectButtonStyles};
                    flex: 1;
                  `}
                >
                  <span>Configure</span>
                  <Icon svg={<Icons.ArrowRight />} />
                </LinkButton>
                <Button
                  variant="danger"
                  size="M"
                  css={css`
                    border-radius: 14px;
                    font-weight: 600;
                  `}
                  onPress={() => handleStopEvaluation("output-drift")}
                >
                  Stop
                </Button>
              </div>
            ) : (
              <LinkButton 
                to="/templates/dataset/output-drift" 
                css={selectButtonStyles}
              >
                <span>Configure Template</span>
                <Icon svg={<Icons.ArrowRight />} />
              </LinkButton>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
